import React, { Component } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import './Dashboard.scss';

class FeeDecisionDashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            refreshing: false,
            
            // KPI Metrics (Spec-compliant only)
            kpiMetrics: {
                totalOutstanding: 0,
                monthToDateCollection: 0,
                monthCollectionRate: 0,
                promisesDueThisWeek: 0
            },
            
            // Analytics Data (Only required charts)
            overdueStudents: [],
            
            // Management Decision Data
            riskBreakdown: {
                CRITICAL: { count: 0, amount: 0 },
                HIGH: { count: 0, amount: 0 },
                MEDIUM: { count: 0, amount: 0 },
                LOW: { count: 0, amount: 0 },
                highRiskCount: 0,
                criticalRiskCount: 0,
                highRiskFormatted: '0 High Risk (0 Critical)'
            },
            predictions: {
                totalPredicted: 0,
                expectedCollection: 0,
                atRiskAmount: 0,
                confidenceScore: 0
            },
            criticalAlerts: [],
            pendingApprovals: [],
            recentActivity: [],
            escalatedCases: [],
            followupPerformance: {
                completed: 0,
                total: 0,
                percentage: 0,
                formatted: '0% (0 of 0)'
            },
            
            // Dialogs
            overrideDialogVisible: false,
            selectedAction: null,
            overrideJustification: ''
        };
        
        this.service = new Service();
        this.toast = React.createRef();
        this.refreshInterval = null;
    }

    componentDidMount() {
        this.fetchDashboardData();
        
        // Auto-refresh every 5 minutes
        this.refreshInterval = setInterval(() => {
            this.refreshDashboard();
        }, 5 * 60 * 1000);
    }

    componentWillUnmount() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({
            severity: severity,
            summary: summary,
            detail: detail,
            life: 3000
        });
    }

    fetchDashboardData = async () => {
        this.setState({ loading: true });
        try {
            const orgId = localStorage.getItem('orgId');
            const academicYear = localStorage.getItem('userAcademicYear');
            
            if (!orgId) {
                this.showToast('error', 'Error', 'Organization ID not found');
                this.setState({ loading: false });
                return;
            }

            const startDate = new Date(new Date().getFullYear() - 1, 3, 1).toISOString();
            const endDate = new Date().toISOString();

            const url = `${baseUrlAdmin}/fee-decision/dashboard/overview?orgId=${orgId}&academicYear=${academicYear}&startDate=${startDate}&endDate=${endDate}`;
            console.log('Fetching dashboard data from:', url);

            const response = await this.service.get(url, true);
            
            if (response?.status && response?.res?.data) {
                const data = response.res.data;
                console.log('Dashboard data received:', data);
                console.log('Critical Alerts:', data.criticalAlerts);
                console.log('Pending Approvals:', data.pendingApprovals);
                console.log('Recent Activity:', data.recentActivity);
                console.log('Overdue Students:', data.overdueStudents);
                console.log('Predictions:', data.predictions);
                
                this.setState({
                    kpiMetrics: data.kpiMetrics || {},
                    overdueStudents: data.overdueStudents || [],
                    riskBreakdown: data.riskBreakdown || {},
                    predictions: data.predictions || {},
                    criticalAlerts: data.criticalAlerts || [],
                    pendingApprovals: data.pendingApprovals || [],
                    recentActivity: data.recentActivity || [],
                    escalatedCases: data.escalatedCases || [],
                    followupPerformance: data.followupPerformance || {}
                });
            } else {
                console.error('Invalid response format:', response);
                this.showToast('warn', 'Warning', 'Unable to load dashboard data');
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            this.showToast('error', 'Error', 'Failed to load dashboard data');
        } finally {
            this.setState({ loading: false });
        }
    }

    refreshDashboard = async () => {
        this.setState({ refreshing: true });
        await this.fetchDashboardData();
        this.setState({ refreshing: false });
        this.showToast('success', 'Success', 'Dashboard refreshed');
    }

    formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0';
        return `₹${Math.round(amount).toLocaleString('en-IN')}`;
    }

    formatPercentage = (value) => {
        if (!value && value !== 0) return '0%';
        return `${Math.round(value)}%`;
    }

    // Navigation handlers
    handleKPICardClick = (metric) => {
        switch (metric) {
            case 'totalOutstanding':
            case 'overdueStudentsCount':
                this.props.navigate('/financeNew/FeeLedger');
                break;
            case 'criticalRisk':
                this.handleRiskCardClick('CRITICAL');
                break;
            case 'highRisk':
                this.handleRiskCardClick('HIGH');
                break;
            case 'promisesDueThisWeek':
                this.props.navigate('/financeNew/ActionQueue');
                break;
            default:
                break;
        }
    }

    handleRiskCardClick = (riskBand) => {
        this.props.navigate(`/financeNew/RiskOverview?riskBand=${riskBand}`);
    }

    handleAlertClick = (alert) => {
        switch (alert.type) {
            case 'CRITICAL_RISK':
            case 'PROMISE_BREACH':
                // Navigate to student's fee ledger
                if (alert.studentId) {
                    this.props.navigate(`/financeNew/FeeLedger?studentId=${alert.studentId}`);
                }
                break;
                
            case 'ESCALATION_APPROVAL':
                // Navigate to action queue approval page
                this.props.navigate('/financeNew/ActionApprovals');
                break;
                
            case 'COMMUNICATION_FAILURE':
                // Navigate to notification logs or settings
                this.showToast('info', 'Alert', 'Please check notification settings and logs in Admin panel');
                break;
                
            case 'DATA_INTEGRITY':
                // Navigate to student management or show info
                this.showToast('warn', 'Data Issue', 'Please review student fee assignments in Finance module');
                break;
                
            default:
                if (alert.studentId) {
                    this.props.navigate(`/financeNew/FeeLedger?studentId=${alert.studentId}`);
                }
                break;
        }
    }

    // Approval actions - Navigate to ApprovalRequests page
    handleApproveAction = (action) => {
        // Navigate to Approval Requests page where user can approve
        this.props.navigate('/financeNew/ApprovalRequests');
    }

    handleOverrideAction = (action) => {
        // Navigate to Approval Requests page where user can override
        this.props.navigate('/financeNew/ApprovalRequests');
    }

    handleEscalationReview = (escalation) => {
        // Navigate to Approval Requests page for full approval workflow
        if (escalation.rawStatus === 'NEEDS_APPROVAL') {
            this.props.navigate('/financeNew/ApprovalRequests');
        } else {
            // For active escalations, navigate to student ledger
            if (escalation.studentId) {
                this.props.navigate(`/financeNew/FeeLedger?studentId=${escalation.studentId}`);
            }
        }
    }

    submitApproval = async (actionId, decision, justification) => {
        this.setState({ loading: true });
        
        try {
            const url = `${baseUrlAdmin}/fee-decision/actions/${actionId}`;
            
            const response = await this.service.put(url, {
                status: decision,
                justification
            }, true);
            
            if (response?.status && response?.res?.success) {
                this.showToast('success', 'Success', `Action ${decision.toLowerCase()} successfully`);
                
                this.setState(prevState => ({
                    pendingApprovals: prevState.pendingApprovals.filter(a => a._id !== actionId),
                    escalatedCases: prevState.escalatedCases.filter(e => e.actionId !== actionId),
                    overrideDialogVisible: false,
                    selectedAction: null,
                    overrideJustification: ''
                }));
                
                this.fetchDashboardData();
            } else {
                this.showToast('error', 'Error', 'Failed to process approval');
            }
        } catch (error) {
            console.error('Error processing approval:', error);
            this.showToast('error', 'Error', 'Failed to process approval');
        } finally {
            this.setState({ loading: false });
        }
    }

    handleOverrideSubmit = () => {
        const { selectedAction, overrideJustification } = this.state;
        
        if (!overrideJustification || overrideJustification.trim().length < 10) {
            this.showToast('warn', 'Validation', 'Please provide a justification (minimum 10 characters)');
            return;
        }
        
        this.submitApproval(selectedAction._id, 'OVERRIDDEN', overrideJustification);
    }

    // Chart data preparation
    prepareRiskBreakdownChartData = () => {
        const { riskBreakdown } = this.state;
        return {
            labels: ['Critical', 'High', 'Medium', 'Low'],
            datasets: [{
                data: [
                    riskBreakdown.CRITICAL?.count || 0,
                    riskBreakdown.HIGH?.count || 0,
                    riskBreakdown.MEDIUM?.count || 0,
                    riskBreakdown.LOW?.count || 0
                ],
                backgroundColor: ['#4A3AFF', '#962DFF', '#93AAFD', '#E0C6FD'],
                borderWidth: 0
            }]
        };
    }

    preparePredictionChartData = () => {
        const { predictions } = this.state;
        
        // Get current month and next 2 months
        const months = [];
        const today = new Date();
        for (let i = 0; i < 3; i++) {
            const monthDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
            months.push(monthDate.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }));
        }
        
        // Use API-provided monthly breakdown (calculated from days30, days60, days90)
        const monthlyExpected = predictions.monthlyBreakdown?.expected || [0, 0, 0];
        const monthlyAtRisk = predictions.monthlyBreakdown?.atRisk || [0, 0, 0];
        
        return {
            labels: months,
            datasets: [
                {
                    label: 'Expected Collection',
                    data: monthlyExpected,
                    backgroundColor: '#4A3AFF',
                    borderWidth: 0,
                    borderRadius: 4
                },
                {
                    label: 'At Risk Amount',
                    data: monthlyAtRisk,
                    backgroundColor: '#962DFF',
                    borderWidth: 0,
                    borderRadius: 4
                }
            ]
        };
    }

    getChartOptions = () => {
        return {
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        font: { family: 'InterMedium', size: 12 }
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            const label = context.dataset.label || '';
                            const value = context.parsed.y;
                            return `${label}: ${this.formatCurrency(value)}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    grid: { display: true, color: '#E5E5EF' },
                    ticks: { 
                        font: { family: 'InterRegular', size: 11 },
                        maxRotation: 45,
                        minRotation: 45
                    }
                },
                y: {
                    grid: { display: false },
                    ticks: {
                        font: { family: 'InterRegular', size: 11 },
                        callback: (value) => this.formatCurrency(value)
                    }
                }
            }
        };
    }

    getPieChartOptions = () => {
        return {
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: { font: { family: 'InterMedium', size: 12 } }
                },
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            const label = context.label || '';
                            const value = context.parsed;
                            return `${label}: ${value}`;
                        }
                    }
                }
            }
        };
    }

    getBarChartOptions = () => {
        return {
            maintainAspectRatio: false,
            responsive: true,
            layout: {
                padding: {
                    bottom: 10,
                    left: 10,
                    right: 10
                }
            },
            plugins: {
                legend: {
                    position: 'top',
                    align: 'end',
                    labels: { 
                        font: { family: 'InterMedium', size: 12 },
                        usePointStyle: true,
                        padding: 15
                    }
                },
                tooltip: {
                    enabled: true,
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                        title: (context) => {
                            return context[0].label;
                        },
                        label: (context) => {
                            const value = context.parsed.y;
                            if (value === 0) {
                                return `${context.dataset.label}: ₹0 (No installments due)`;
                            } else if (value < 50000) {
                                return `${context.dataset.label}: ${this.formatCurrency(value)} (Minimal amount)`;
                            }
                            return `${context.dataset.label}: ${this.formatCurrency(value)}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: { 
                        font: { family: 'InterMedium', size: 12 },
                        autoSkip: false,
                        maxRotation: 0,
                        minRotation: 0,
                        padding: 10
                    }
                },
                y: {
                    beginAtZero: true,
                    grid: { display: true, color: '#E5E5EF' },
                    ticks: {
                        font: { family: 'InterRegular', size: 11 },
                        callback: (value) => this.formatCurrency(value)
                    }
                }
            },
            barPercentage: 0.7,
            categoryPercentage: 0.8
        };
    }

    // Render KPI Cards
    renderKPICards = () => {
        const { kpiMetrics, riskBreakdown, predictions } = this.state;

        // Spec-compliant KPI cards - Row 1
        const kpiCardsRow1 = [
            {
                icon: 'pi pi-dollar',
                label: 'Total Outstanding Fees',
                value: this.formatCurrency(kpiMetrics.totalOutstanding),
                key: 'totalOutstanding',
                type: 'critical'
            },
            {
                icon: 'pi pi-calendar-plus',
                label: 'Month-to-Date Collection',
                value: this.formatCurrency(kpiMetrics.monthToDateCollection),
                key: 'monthToDateCollection',
                type: 'success'
            },
            {
                icon: 'pi pi-forward',
                label: 'Predicted 30-Day Inflow',
                value: this.formatCurrency(predictions.expectedCollection),
                key: 'predicted30Day',
                type: 'default'
            }
        ];

        // Row 2
        const kpiCardsRow2 = [
            {
                icon: 'pi pi-exclamation-triangle',
                label: 'High Risk Students',
                value: riskBreakdown.highRiskFormatted || `${riskBreakdown.highRiskCount || 0} High Risk (${riskBreakdown.criticalRiskCount || 0} Critical)`,
                key: 'highRisk',
                type: 'warning'
            },
            {
                icon: 'pi pi-clock',
                label: 'Promises Due This Week',
                value: kpiMetrics.promisesDueThisWeek || 0,
                key: 'promisesDueThisWeek',
                type: 'default'
            }
        ];

        const renderRow = (cards) => (
            <div className="cards-row">
                {cards.map(card => (
                    <div 
                        key={card.key} 
                        className={`kpi-card ${card.type}`}
                        onClick={() => this.handleKPICardClick(card.key)}
                    >
                        <div className="kpi-icon-group">
                            <i className={`kpi-icon ${card.icon}`}></i>
                            <span className="kpi-label">{card.label}</span>
                        </div>
                        <div className="kpi-divider"></div>
                        <div className="kpi-value">{card.value}</div>
                    </div>
                ))}
            </div>
        );

        return (
            <div className="kpi-cards-container">
                {renderRow(kpiCardsRow1)}
                {renderRow(kpiCardsRow2)}
            </div>
        );
    }

    // Render Risk Breakdown
    renderRiskBreakdown = () => {
        const { riskBreakdown } = this.state;

        return (
            <div className="risk-breakdown-section">
                <h3 className="section-header">Risk Breakdown</h3>
                <div className="risk-grid">
                    <div className="risk-card critical" onClick={() => this.handleRiskCardClick('CRITICAL')}>
                        <div className="risk-label">CRITICAL</div>
                        <div className="risk-count">{riskBreakdown.CRITICAL?.count || 0}</div>
                        <div className="risk-amount">{this.formatCurrency(riskBreakdown.CRITICAL?.amount)}</div>
                    </div>
                    <div className="risk-card high" onClick={() => this.handleRiskCardClick('HIGH')}>
                        <div className="risk-label">HIGH</div>
                        <div className="risk-count">{riskBreakdown.HIGH?.count || 0}</div>
                        <div className="risk-amount">{this.formatCurrency(riskBreakdown.HIGH?.amount)}</div>
                    </div>
                    <div className="risk-card medium" onClick={() => this.handleRiskCardClick('MEDIUM')}>
                        <div className="risk-label">MEDIUM</div>
                        <div className="risk-count">{riskBreakdown.MEDIUM?.count || 0}</div>
                        <div className="risk-amount">{this.formatCurrency(riskBreakdown.MEDIUM?.amount)}</div>
                    </div>
                    <div className="risk-card low" onClick={() => this.handleRiskCardClick('LOW')}>
                        <div className="risk-label">LOW</div>
                        <div className="risk-count">{riskBreakdown.LOW?.count || 0}</div>
                        <div className="risk-amount">{this.formatCurrency(riskBreakdown.LOW?.amount)}</div>
                    </div>
                </div>
            </div>
        );
    }

    // Render Charts
    renderCharts = () => {
        const riskData = this.prepareRiskBreakdownChartData();
        const predictionData = this.preparePredictionChartData();

        return (
            <div className="charts-section">
                {/* Collections Forecast - 50% Width */}
                <div className="chart-card">
                    <div className="chart-header">
                        <span>3-Month Collections Forecast</span>
                        <span className="chart-subtitle">Expected vs At-Risk Collections</span>
                    </div>
                    <div className="chart-body">
                        <Chart 
                            type="bar" 
                            data={predictionData} 
                            options={this.getBarChartOptions()} 
                            style={{ width: '100%', height: '100%' }}
                        />
                        {/* Info note for small/zero values */}
                        {(this.state.predictions.monthlyBreakdown?.expected || []).some((val, idx) => idx > 0 && val < 50000) && (
                            <div style={{ 
                                fontSize: '10px', 
                                color: '#666', 
                                textAlign: 'center', 
                                marginTop: '5px',
                                fontFamily: 'InterRegular'
                            }}>
                                <i className="pi pi-info-circle" style={{ fontSize: '10px', marginRight: '4px' }}></i>
                                Hover over bars to see exact values (some amounts too small to display)
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Risk Distribution Chart - 50% Width */}
                <div className="chart-card">
                    <div className="chart-header">Risk Distribution</div>
                    <div className="chart-body">
                        <Chart 
                            type="pie" 
                            data={riskData} 
                            options={this.getPieChartOptions()} 
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Render Critical Alerts
    renderAlerts = () => {
        const { criticalAlerts } = this.state;

        if (criticalAlerts.length === 0) {
            return (
                <div className="alerts-section">
                    <div className="section-header">
                        <span>Critical Alerts</span>
                        <span className="alert-count">0</span>
                    </div>
                    <div className="empty-state">No critical alerts at the moment</div>
                </div>
            );
        }

        // Helper to get icon for alert type
        const getAlertIcon = (type) => {
            switch (type) {
                case 'CRITICAL_RISK':
                    return 'pi pi-exclamation-triangle';
                case 'PROMISE_BREACH':
                    return 'pi pi-calendar-times';
                case 'ESCALATION_APPROVAL':
                    return 'pi pi-arrow-up';
                case 'COMMUNICATION_FAILURE':
                    return 'pi pi-send';
                case 'DATA_INTEGRITY':
                    return 'pi pi-database';
                default:
                    return 'pi pi-info-circle';
            }
        };

        return (
            <div className="alerts-section">
                <div className="section-header">
                    <span>Critical Alerts</span>
                    <span className="alert-count">{criticalAlerts.length}</span>
                </div>
                <div className="alert-list">
                    {criticalAlerts.map((alert, index) => (
                        <div 
                            key={index} 
                            className={`alert-item ${alert.severity === 'HIGH' ? '' : alert.severity === 'MEDIUM' ? 'warning' : 'info'}`}
                            onClick={() => this.handleAlertClick(alert)}
                        >
                            <div className="alert-content">
                                <div className="alert-title">
                                    <i className={getAlertIcon(alert.type)} style={{ marginRight: '8px' }}></i>
                                    {alert.title}
                                </div>
                                <div className="alert-description">{alert.description}</div>
                            </div>
                            <div className="alert-action">
                                <Authorizer permId={PERMISSIONS.FDE_REVIEW_ALERT}>
                                    <Button 
                                        label="Review Now" 
                                        icon="pi pi-arrow-right" 
                                        className="p-button-sm p-button-outlined"
                                    />
                                </Authorizer>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Render Pending Approvals
    renderApprovals = () => {
        const { pendingApprovals } = this.state;

        if (pendingApprovals.length === 0) {
            return (
                <div className="approvals-section">
                    <h3 className="section-header">Pending Approvals</h3>
                    <div className="empty-state">No pending approvals</div>
                </div>
            );
        }

        return (
            <div className="approvals-section">
                <h3 className="section-header">Pending Approvals ({pendingApprovals.length})</h3>
                <div className="approval-list">
                    {pendingApprovals.map((approval) => (
                        <div key={approval._id} className="approval-item">
                            <div className="approval-info">
                                <div className="student-name">
                                    {approval.admissionNo}
                                </div>
                                <div className="action-details">
                                    {approval.actionType} - Scheduled: {new Date(approval.scheduledDate).toLocaleDateString()}
                                    <span className={`risk-badge ${approval.riskBand?.toLowerCase()}`}>
                                        {approval.riskBand}
                                    </span>
                                </div>
                            </div>
                            <div className="approval-actions">
                                <Authorizer permId={PERMISSIONS.FDE_APPROVE_ACTION}>
                                    <Button 
                                        label="Approve" 
                                        icon="pi pi-check" 
                                        className="p-button-success p-button-sm"
                                        onClick={() => this.handleApproveAction(approval)}
                                    />
                                </Authorizer>
                                <Authorizer permId={PERMISSIONS.FDE_MANUAL_ACTION_OVERRIDE}>
                                    <Button 
                                        label="Override" 
                                        icon="pi pi-times" 
                                        className="p-button-danger p-button-sm"
                                        onClick={() => this.handleOverrideAction(approval)}
                                    />
                                </Authorizer>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Render Recent Activity
    renderActivity = () => {
        const { recentActivity } = this.state;

        if (recentActivity.length === 0) {
            return (
                <div className="activity-section">
                    <h3 className="section-header">Recent Activity</h3>
                    <div className="empty-state">No recent activity</div>
                </div>
            );
        }

        return (
            <div className="activity-section">
                <h3 className="section-header">Recent Activity (Last 7 Days)</h3>
                <div className="activity-feed">
                    {recentActivity.map((activity, index) => (
                        <div key={index} className="activity-item">
                            <div className={`activity-icon ${activity.type.toLowerCase()}`}>
                                <i className={
                                    activity.type === 'APPROVAL' ? 'pi pi-check' :
                                    activity.type === 'PAYMENT' ? 'pi pi-money-bill' :
                                    'pi pi-info-circle'
                                }></i>
                            </div>
                            <div className="activity-content">
                                <div className="activity-description">{activity.description}</div>
                                <div className="activity-timestamp">
                                    {new Date(activity.timestamp).toLocaleString()}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Render Top Overdue Students Table
    renderOverdueTable = () => {
        const { overdueStudents } = this.state;

        const amountBodyTemplate = (rowData) => {
            return this.formatCurrency(rowData.overdueAmount);
        };

        const daysBodyTemplate = (rowData) => {
            return `${rowData.overdueDays} days`;
        };

        return (
            <div className="overdue-table-section">
                <h3 className="section-header">Top Overdue Students</h3>
                <DataTable 
                    value={overdueStudents} 
                    emptyMessage="No overdue students"
                >
                    <Column field="studentName" header="Student Name" sortable />
                    <Column field="admissionNo" header="Admission No" sortable />
                    <Column field="className" header="Class" sortable />
                    <Column field="overdueAmount" header="Overdue Amount" body={amountBodyTemplate} sortable />
                    <Column field="overdueDays" header="Days Overdue" body={daysBodyTemplate} sortable />
                </DataTable>
            </div>
        );
    }

    // Render Escalated Cases
    renderEscalatedCases = () => {
        const { escalatedCases } = this.state;

        if (escalatedCases.length === 0) {
            return (
                <div className="escalated-section">
                    <h3 className= "section-header">Escalated Cases</h3>
                    <div className="empty-state">No escalated cases</div>
                </div>
            );
        }

        return (
            <div className="escalated-section">
                <h3 className="section-header">Escalated Cases ({escalatedCases.length})</h3>
                <div className="escalated-list">
                    {escalatedCases.map((escalation, index) => (
                        <div key={index} className="escalated-item">
                            <div className="escalated-info">
                                <div className="student-name">
                                    {escalation.enrollmentNo || escalation.admissionNo}
                                </div>
                                <div className="escalation-details">
                                    Handler: {escalation.handler} | Status: <span className={`status-badge ${escalation.rawStatus === 'NEEDS_APPROVAL' ? 'status-warning' : 'status-active'}`}>{escalation.status}</span>
                                    <span className={`risk-badge ${escalation.riskBand?.toLowerCase()}`}>
                                        {escalation.riskBand} | Score: {escalation.riskScore}
                                    </span>
                                </div>
                                <div className="escalation-reason">
                                    {escalation.triggerReason}
                                </div>
                            </div>
                            {escalation.rawStatus === 'NEEDS_APPROVAL' && (
                                <div className="escalation-action">
                                    <Authorizer permId={PERMISSIONS.FDE_APPROVE_ESCALATION}>
                                        <Button 
                                            label="Review & Approve" 
                                            icon="pi pi-arrow-right" 
                                            className="p-button-sm p-button-success"
                                            onClick={() => this.handleEscalationReview(escalation)}
                                        />
                                    </Authorizer>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Render Follow-up Performance
    renderFollowupPerformance = () => {
        const { followupPerformance } = this.state;

        return (
            <div className="followup-performance-section">
                <h3 className="section-header">Follow-up Performance (Today)</h3>
                <div className="performance-card">
                    <div className="performance-value">
                        {followupPerformance.formatted || '0% (0 of 0)'}
                    </div>
                    <div className="performance-detail">
                        Follow-ups completed: {followupPerformance.completed} of {followupPerformance.total}
                    </div>
                    <div className="performance-bar">
                        <div 
                            className="performance-fill" 
                            style={{ width: `${followupPerformance.percentage}%` }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { loading, refreshing, overrideDialogVisible, overrideJustification } = this.state;

        const containerStyle = {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#E6E8EA',
            padding: window.innerWidth > 1090 ? "50px 0px 0px 95px" : "50px 0px 0px 15px",
            overflow: 'auto',
            transition: 'padding 0.3s',
            paddingBottom: '30px',
        };

        if (loading && !refreshing) {
            return (
                <div style={containerStyle}>
                    <LoadingComponent />
                </div>
            );
        }

        return (
            <div style={containerStyle}>
                <div className="fee-decision-dashboard">
                    <Toast ref={this.toast} position="bottom-right" />
                    <ConfirmDialog />

                <div className="dashboard-header">
                    <h1 className="dashboard-title">Fee Decision Dashboard</h1>
                    <div className="dashboard-actions">
                        <Authorizer permId={PERMISSIONS.FDE_DASHBOARD}>
                            <Button 
                                icon="pi pi-refresh" 
                                label="Refresh" 
                                className="p-button-outlined"
                                onClick={this.refreshDashboard}
                                loading={refreshing}
                            />
                        </Authorizer>
                    </div>
                </div>

                {this.renderKPICards()}
                {this.renderRiskBreakdown()}
                {this.renderCharts()}
                {this.renderAlerts()}
                {this.renderApprovals()}
                {this.renderEscalatedCases()}
                
                {/* Follow-up Performance and Recent Activity Side-by-Side */}
                <div className="performance-activity-row">
                    {this.renderFollowupPerformance()}
                    {this.renderActivity()}
                </div>
                
                {this.renderOverdueTable()}

                {/* Override Dialog */}
                <Dialog
                    visible={overrideDialogVisible}
                    onHide={() => this.setState({ overrideDialogVisible: false, selectedAction: null })}
                    header="Override Action"
                    style={{ width: '500px' }}
                    footer={
                        <div>
                            <Button 
                                label="Cancel" 
                                icon="pi pi-times" 
                                className="p-button-text"
                                onClick={() => this.setState({ overrideDialogVisible: false, selectedAction: null })}
                            />
                            <Authorizer permId={PERMISSIONS.FDE_MANUAL_ACTION_OVERRIDE}>
                                <Button 
                                    label="Submit Override" 
                                    icon="pi pi-check" 
                                    className="p-button-warning"
                                    onClick={this.handleOverrideSubmit}
                                />
                            </Authorizer>
                        </div>
                    }
                >
                    <div>
                        <label style={{ fontFamily: 'InterMedium', marginBottom: '0.5rem', display: 'block' }}>
                            Justification (Required - minimum 10 characters)
                        </label>
                        <InputTextarea
                            value={overrideJustification}
                            onChange={(e) => this.setState({ overrideJustification: e.target.value })}
                            rows={5}
                            style={{ width: '100%', fontFamily: 'InterRegular' }}
                            placeholder="Explain why you are overriding this action..."
                        />
                    </div>
                </Dialog>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    orgId: state.orgId,
    authenticated: state.authenticated
});

// HOC to provide navigate function to class component
const withNavigate = (Component) => {
    return (props) => {
        const navigate = useNavigate();
        return <Component {...props} navigate={navigate} />;
    };
};

export default connect(mapStateToProps)(withNavigate(FeeDecisionDashboard));
